import type { SystemProps } from '@hover/blueprint';
import {
  Box,
  Button,
  Heading,
  IconButton,
  useBreakpointValue,
} from '@hover/blueprint';
import { iChevronDown, iconEllipsis } from '@hover/icons';
import React from 'react';

import { CobrandLogo } from 'src/modules/homeownerApp/components/CobrandLogo/CobrandLogo';
import { PROJECT_DETAILS_PADDING } from 'src/modules/homeownerApp/components/Projects/Project/ProjectCardSection';
import { ProjectShareButton } from 'src/modules/homeownerApp/components/Projects/Project/ProjectShareButton';
import { TopBar } from 'src/modules/homeownerApp/components/TopBar';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { usePartnerCobranding } from 'src/modules/homeownerApp/hooks/usePartner';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import type { Project as ProjectType } from 'src/shared/graphql/generated';

import {
  DESKTOP_HEADER_HEIGHT,
  TOP_BAR_NAME_CHACACTER_LIMIT,
} from './static_values';

const formatTopProjectName = (name: string) =>
  name.length > TOP_BAR_NAME_CHACACTER_LIMIT
    ? `${name.slice(0, TOP_BAR_NAME_CHACACTER_LIMIT)}...`
    : name;

interface ProjectHeaderProps {
  background?: string;
  hasImages?: boolean;
  id: ProjectType['id'];
  name: string;
  onMoreActionsClick: () => void;
  projectNameVisibilityHandler?: React.RefObject<HTMLDivElement>;
}

const ProjectHeader = React.forwardRef<HTMLDivElement, ProjectHeaderProps>(
  ({ background, hasImages, id, name, onMoreActionsClick }, ref) => {
    const isMobile = useBreakpointValue({ base: true, tablet: false });
    const { setCurrentMenu } = useHomeownerAppContext();

    const cobranding = usePartnerCobranding();

    const topbarProps = {
      width: '100%',
      display: { base: 'flex', tablet: 'none' },
      bottom: 'unset',
      backgroundColor: 'unset',
    } satisfies SystemProps;

    return (
      <Box
        background={background}
        flexDirection={{ base: 'column', tablet: 'row' }}
        left={0}
        paddingX={PROJECT_DETAILS_PADDING}
        ref={ref}
        right={0}
        sx={{
          transition: 'background-color 1s ease',
        }}
        top={0}
        width="100%"
        zIndex={{ base: 'sticky', tablet: 'unset' }}
      >
        {cobranding && (
          <CobrandLogo
            colorMode="dark"
            display={{ base: 'flex', tablet: 'none' }}
            mb={-200}
            mt={400}
            width="100%"
          />
        )}
        <Box width="100%">
          <CobrandLogo
            colorMode="dark"
            display={{ base: 'none', tablet: 'flex' }}
            width="100%"
          />
          {/* Project Selection Button */}
          <Box
            alignItems="center"
            display="flex"
            height={{ tablet: DESKTOP_HEADER_HEIGHT }}
            justifyContent="flex-start"
            zIndex={1}
          >
            <Button
              colorScheme="light"
              iconAfter={iChevronDown}
              label={name}
              onClick={() =>
                setCurrentMenu(HomeownerAppMenuNames.PROJECT_SELECTION)
              }
              px={{ base: 0, tablet: 500 }}
              variant="ghost"
            >
              <Heading color="inherit" m={0} textStyle="headingSmall">
                {formatTopProjectName(name)}
              </Heading>
            </Button>
          </Box>
          {/* Mobile Top Bar */}
          {isMobile && (
            <TopBar
              moreActionsHandler={() => onMoreActionsClick()}
              project
              projectId={id}
              showContent={false}
              {...topbarProps}
            >
              <Button
                colorScheme="light"
                marginLeft={800}
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                size="small"
                variant="ghost"
                w="100%"
              >
                {formatTopProjectName(name)}
              </Button>
            </TopBar>
          )}
          <Box
            alignItems="center"
            display={{ base: 'none', tablet: 'flex' }}
            gap={200}
            height={DESKTOP_HEADER_HEIGHT}
            justifyContent="flex-end"
            w="100%"
            zIndex={1}
          >
            <ProjectShareButton
              colorScheme={background ? 'light' : 'dark'}
              disabled={!hasImages}
              id={id}
            />
            <IconButton
              colorScheme={background ? 'light' : 'dark'}
              icon={iconEllipsis}
              label="moreActions"
              onClick={() => onMoreActionsClick()}
              variant={{ base: 'ghost', tablet: 'glass' }}
            />
          </Box>
        </Box>
      </Box>
    );
  },
);

export { ProjectHeader };
